import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { v4 as uuidv4 } from "uuid";

// Sample job titles
const jobTitles = [
    "Frontend Developer",
    "Backend Engineer",
    "DevOps Specialist",
    "Full Stack Developer",
    "Data Scientist",
    "UX/UI Designer",
    "Product Manager",
    "QA Engineer",
    "Mobile Developer",
    "Machine Learning Engineer",
    "Cloud Architect",
    "Database Administrator",
    "Solutions Architect",
    "Technical Project Manager",
    "Business Analyst"
];

// Sample companies (already existing in the database)
const companies = [
    { id: "company-1", name: "Acme Corp" },
    { id: "company-2", name: "Tech Innovations" },
    { id: "company-3", name: "Digital Solutions" },
    { id: "company-4", name: "Cloud Systems" },
    { id: "company-5", name: "Data Analytics Inc" }
];

// Sample skills for developers
const techSkills = [
    "JavaScript", "TypeScript", "React", "Angular", "Vue", "Node.js",
    "Python", "Java", "C#", ".NET", "AWS", "Azure", "Docker", "Kubernetes",
    "GraphQL", "REST API", "MongoDB", "PostgreSQL", "SQL", "NoSQL",
    "CI/CD", "Git", "Redux", "PHP", "Laravel", "Ruby", "Rails",
    "Swift", "Kotlin", "Flutter", "React Native", "Go", "Rust"
];

// Sample candidates (these would be real candidates in your application)
const candidateNames = [
    "Amelia Torres", "Benjamin Smith", "Ava Adams", "Ethan Williams",
    "Olivia Johnson", "Lucas Brown", "Sophia Martinez", "Jackson Lee",
    "Emma Davis", "Noah Wilson", "Isabella Anderson", "Liam Thomas"
];

// Generate a random job description
const generateJobDescription = (title: string) => {
    return `
We are looking for an experienced ${title} to join our dynamic team. The ideal candidate will have strong problem-solving skills and a passion for creating efficient, scalable solutions.

Responsibilities:
- Design, develop, and maintain software applications
- Collaborate with cross-functional teams
- Implement best practices and coding standards
- Troubleshoot and debug issues
- Stay up-to-date with emerging trends and technologies

Requirements:
- 3+ years of experience in ${title.toLowerCase()} roles
- Strong understanding of software development principles
- Excellent communication and teamwork skills
- Bachelor's degree in Computer Science or related field (or equivalent experience)
  `;
};

// Generate random skills array for a job
const generateRandomSkills = (count: number) => {
    const shuffled = [...techSkills].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
};

// Generate random matches for a job
const generateRandomMatches = (jobId: string, count: number) => {
    const matches = [];
    const shuffledCandidates = [...candidateNames].sort(() => 0.5 - Math.random());

    for (let i = 0; i < Math.min(count, shuffledCandidates.length); i++) {
        matches.push({
            id: uuidv4(),
            job_id: jobId,
            candidate_id: `candidate-${i + 1}`,
            candidate_name: shuffledCandidates[i],
            score: Math.floor(Math.random() * 31) + 70, // Score between 70-100
            created_at: new Date().toISOString()
        });
    }

    return matches;
};

// Define job candidate stages
const stages = ["applied", "screening", "interview", "offer", "hired", "rejected"];

// Generate kanban data for a job
const generateKanbanData = (jobId: string, candidateIds: string[]) => {
    const result = [];

    const shuffledCandidates = [...candidateIds].sort(() => 0.5 - Math.random());

    for (let i = 0; i < shuffledCandidates.length; i++) {
        const randomStageIndex = Math.floor(Math.random() * (stages.length - 1)); // -1 to make "rejected" less common
        result.push({
            job_id: jobId,
            candidate_id: shuffledCandidates[i],
            status: stages[randomStageIndex],
            score: Math.floor(Math.random() * 31) + 70, // Score between 70-100
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
        });
    }

    return result;
};

// Main function to generate and insert dummy jobs
export const generateDummyJobs = async (count: number = 5) => {
    try {
        const shuffledTitles = [...jobTitles].sort(() => 0.5 - Math.random());
        const jobsToInsert = [];

        // Create jobs data
        for (let i = 0; i < count; i++) {
            const title = shuffledTitles[i % shuffledTitles.length];
            const randomCompany = companies[Math.floor(Math.random() * companies.length)];
            const skills = generateRandomSkills(Math.floor(Math.random() * 5) + 3); // 3-7 skills

            jobsToInsert.push({
                id: uuidv4(),
                title,
                description: generateJobDescription(title),
                company_id: randomCompany.id,
                skills,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            });
        }

        // Insert jobs
        const { data: insertedJobs, error: jobsError } = await supabase
            .from('jobs')
            .insert(jobsToInsert)
            .select();

        if (jobsError) {
            console.error('Error inserting jobs:', jobsError);
            toast({
                title: "Error",
                description: "Failed to generate dummy jobs",
                variant: "destructive",
            });
            return null;
        }

        // For each job, generate matches and kanban data
        if (insertedJobs) {
            for (const job of insertedJobs) {
                const matchCount = Math.floor(Math.random() * 5) + 2; // 2-6 matches
                const matches = generateRandomMatches(job.id, matchCount);

                // Insert matches
                const { error: matchesError } = await supabase
                    .from('matches')
                    .insert(matches.map(match => ({
                        id: match.id,
                        job_id: match.job_id,
                        candidate_id: match.candidate_id,
                        score: match.score,
                        created_at: match.created_at
                    })));

                if (matchesError) {
                    console.error('Error inserting matches:', matchesError);
                    toast({
                        title: "Warning",
                        description: "Jobs created but failed to generate matches",
                        variant: "destructive",
                    });
                }

                // Get candidate IDs from matches
                const candidateIds = matches.map(match => match.candidate_id);

                // Since there is no job_candidates table, let's use matches table to store the kanban stage
                // We'll update the matches with a status field
                if (candidateIds.length > 0) {
                    const kanbanData = generateKanbanData(job.id, candidateIds);

                    // For each candidate in kanban data, update their match with a status
                    for (const item of kanbanData) {
                        const { data, error: updateError } = await supabase
                            .from('matches')
                            .update({
                                status: item.status,
                                score: item.score
                            })
                            .eq('job_id', item.job_id)
                            .eq('candidate_id', item.candidate_id)
                            .select();

                        if (updateError) {
                            console.error('Error updating match with status:', updateError);
                        }

                        // also trigger a call to ai-processing
                        await supabase.functions.invoke('ai-processing?command=push-new-analysis', {
                            method: 'POST',
                            body: {
                                entityId: data[0].id, // Use the match ID as entityId
                                entityType: "match",
                                analysisType: "full-match",
                                actionOnComplete: "update-match",
                            }
                        });
                    }
                }
            }

            toast({
                title: "Success",
                description: `${insertedJobs.length} dummy jobs created with candidates and stages`,
            });

            return insertedJobs;
        }

        return null;
    } catch (error) {
        console.error('Error generating dummy jobs:', error);
        toast({
            title: "Error",
            description: "Failed to generate dummy data",
            variant: "destructive",
        });
        return null;
    }
};
